import React, { useEffect, useRef, useState } from "react";
import { Button, Segmented } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import { isAnswered } from "../../utils/interview-utils";
import useAiAssessmentQuestionNavigation from "../../hooks/useAiAssessmentQuestionNavigation";
import "./questionTracker.scss";

const QuestionTracker = () => {
  const segmentedRef = useRef<HTMLDivElement>(null);
  const [canNavigateLeft, setCanNavigateLeft] = useState(false);
  const [canNavigateRight, setCanNavigateRight] = useState(false);

  const {
    navigateToNextQuestion,
    navigateToPreviousQuestion,
    navigateToQuestion,
  } = useAiAssessmentQuestionNavigation();

  const {
    aiAssessmentQuestions,
    currentStep,
    isRoundFinished,
  } = useAIAssessmentContext();

  const totalQuestions = aiAssessmentQuestions.length;

  const checkNavigationState = () => {
    setCanNavigateLeft(currentStep > 0);
    setCanNavigateRight(currentStep < totalQuestions);
  };

  const getQuestionState = (
    questionIndex: number
  ): "answered" | "unanswered" => {
    const question = aiAssessmentQuestions[questionIndex];
    const questionAnswer = aiAssessmentQuestions[questionIndex];

    if (question && questionAnswer) {
      return isAnswered(question) ? "answered" : "unanswered";
    }

    return "unanswered";
  };

  const getCurrentValue = (): string => {
    if (currentStep === -1) return "start";
    if (currentStep === totalQuestions)
      return isRoundFinished ? "result" : "submit";
    return currentStep.toString();
  };

  const submitOption = {
    label: isRoundFinished ? "Result" : "Submit",
    value: isRoundFinished ? "result" : "submit",
    key: isRoundFinished ? "result" : "submit",
    className: currentStep === totalQuestions ? "active" : "",
  };

  const questionOptions = aiAssessmentQuestions.map((question, index) => {
    const questionState = getQuestionState(index);
    const isActive = currentStep === index;

    return {
      label: (
        <span
          className={`question-option ${questionState} ${
            isActive ? "active" : ""
          }`}
        >
          <span className="question-number">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className={`status-indicator ${questionState}`}></span>
        </span>
      ),
      value: index.toString(),
      key: index.toString(),
      className: `question-segment ${questionState} ${
        isActive ? "active" : ""
      }`,
    };
  });

  const allOptions = [...questionOptions, submitOption];

  const scrollToActiveQuestion = () => {
    if (!segmentedRef.current) return;

    const segmentedElement = segmentedRef.current;

    let targetIndex: number;

    if (currentStep === -1) {
      targetIndex = 0;
    } else if (currentStep === totalQuestions) {
      targetIndex = totalQuestions + 1;
    } else {
      targetIndex = currentStep + 1;
    }

    const totalSegments = totalQuestions + 2;

    if (totalSegments <= 1) return;

    const containerWidth = segmentedElement.clientWidth;
    const scrollWidth = segmentedElement.scrollWidth;

    // If content fits in container, no need to scroll
    if (scrollWidth <= containerWidth) return;

    const segmentWidth = scrollWidth / totalSegments;
    const targetPosition = targetIndex * segmentWidth;

    // Calculate scroll position to make the active segment visible
    // Try to center it, but ensure it's always visible
    const centerOffset = containerWidth / 2;
    const leftBoundary = targetPosition - centerOffset;
    const rightBoundary = targetPosition + centerOffset;

    let scrollPosition: number;

    if (leftBoundary < 0) {
      // If we're near the start, scroll to beginning
      scrollPosition = 0;
    } else if (rightBoundary > scrollWidth) {
      // If we're near the end, scroll to end
      scrollPosition = scrollWidth - containerWidth;
    } else {
      // Center the active segment
      scrollPosition = leftBoundary;
    }

    // Ensure scroll position is within bounds
    const maxScroll = scrollWidth - containerWidth;
    const finalScrollPosition = Math.max(
      0,
      Math.min(scrollPosition, maxScroll)
    );

    segmentedElement.scrollTo({
      left: finalScrollPosition,
      behavior: "smooth",
    });
  };

  const handleChange = (value: string) => {
    if (value === "start") {
      navigateToQuestion(-1);
    } else if (value === "submit") {
      navigateToQuestion(totalQuestions);
    } else if (value === "result") {
      navigateToQuestion(totalQuestions);
    } else {
      const questionIndex = parseInt(value, 10);
      if (!isNaN(questionIndex)) {
        navigateToQuestion(questionIndex);
      }
    }
  };

  // Auto-scroll to active question when questions change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      scrollToActiveQuestion();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [currentStep]);

  // Update navigation state when component mounts or questions change
  useEffect(() => {
    checkNavigationState();
  }, [currentStep]);

  return (
    <section className="question-tracker-container">
      <div className="segmented-container">
        {totalQuestions > 1 && (
          <Button
            className="scroll-button scroll-left"
            icon={<LeftOutlined />}
            onClick={navigateToPreviousQuestion}
            disabled={!canNavigateLeft}
            size="small"
            title="Previous Question"
          />
        )}
        <Segmented<string>
          ref={segmentedRef}
          options={allOptions}
          value={getCurrentValue()}
          onChange={handleChange}
          className="question-tracker-segmented"
          shape="round"
          size="large"
        />
        {totalQuestions > 1 && (
          <Button
            className="scroll-button scroll-right"
            icon={<RightOutlined />}
            onClick={navigateToNextQuestion}
            disabled={!canNavigateRight}
            size="small"
            title="Next Question"
          />
        )}
      </div>
    </section>
  );
};

export default QuestionTracker;
